import React, { useState, useEffect } from "react";
import LayoutAccountant from "../../../components/LayoutAccountant";
import { AutoComplete, Table, message, Input, Select } from "antd";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { http } from "../../../services/http";
import moment from "moment";
import { numberWithSpacesIntl } from "../../../utils/numberWithSpaces";
import { useQuery } from "@tanstack/react-query";
import { checkValueToNotNull } from "../../../utils/checkValue";
import { removeSpaces } from "../../../utils/formatMoney";
import { debouncedSet } from "../../../utils/debounce";
import { getToday } from "../../../utils/getDays";

const ConversionAccountantUploaded = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const code = location.pathname.split("/")[2];

  const [loadingData, setLoadingData] = useState([]);

  const [docType, setDocType] = useState("");
  const [bpName, setBpName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState(getToday());
  const [bpNameSearch, setBpNameSearch] = useState("");
  const [currency, setCurrency] = useState("");
  const [currentPage] = useState(0); //Paginationsiz ishlashiga kelishilgan
  const pageSize = "1000"; //Paginationsiz ishlashiga kelishilgan
  const status = "UPLOADED"; //Status

  // We will map data and put some values we need
  const fetchData = async ({ queryKey }) => {
    const [
      _,
      docType,
      status,
      bpOrAcctName,
      pageSize,
      skip,
      startDate,
      endDate,
      docCur,
    ] = queryKey;
    try {
      const { data } = await http.get(
        `api/convertations/drafts?docType=${docType}&status=${status}&bpOrAcctName=${bpOrAcctName}&startDate=${startDate}&endDate=${endDate}&docCur=${docCur}&bpOrAcctCode=${bpOrAcctName}&skip=${skip}&pageSize=${pageSize}`,
      );
      const mappedData = data.data.map((item) => {
        const rubRate =
          item.docCur === "RUB" ? item.intConv / item.paymentSumBp : "";
        const crossRate =
          item.docCur === "RUB" ? item.sapUsdToUzsRate / rubRate : "";
        const usdRate =
          item.docCur === "USD"
            ? item.intConv / item.paymentSumBp
            : item.sapUsdToUzsRate;
        const paymSumCrosUsd =
          item.docCur === "RUB"
            ? item.paymentSumBp / item.crossRate
            : item.paymentSumBp;

        return {
          ...item,
          rubRate: rubRate,
          crossRate: crossRate,
          usdRate: usdRate,
          paymSumCrosUsd: paymSumCrosUsd,
        };
      });
      return mappedData;
    } catch (err) {
      console.error(err?.response?.data?.message);
    }
  };

  const fetchBpName = async ({ queryKey }) => {
    const [_, cardName] = queryKey;
    try {
      const { data } = await http.get(
        `api/bankstatements/business-partners?cardName=${cardName}`,
      );
      return data;
    } catch (err) {
      message.error(err?.response?.data?.message);
    }
  };

  const { data: bpData = [], isLoading: bpIsLoading } = useQuery({
    queryKey: ["bp", bpNameSearch],
    queryFn: fetchBpName,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const {
    data = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      "convertations",
      docType,
      status,
      bpName,
      pageSize,
      currentPage,
      startDate,
      endDate,
      currency,
    ],
    queryFn: fetchData,
    keepPreviousData: true,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (data.length > 0) {
      setLoadingData(Array(data.length).fill(false));
    }
  }, [data]);

  const columns = [
    {
      title: t("paymentDate"),
      dataIndex: "docDate",
      key: "docDate",
      render: (value) =>
        checkValueToNotNull(value, "-", moment(value).format("DD-MM-YYYY")),
    },
    {
      title: t("paymentType"),
      dataIndex: "docType",
      key: "docType",
      render: (value) => switchDocType(value),
    },
    {
      title: t("bp/Acc"),
      dataIndex: "bpOrAcctName",
      key: "bpOrAcctName",
    },
    {
      title: t("convertationCurrency"),
      dataIndex: "docCur",
      key: "docCur",
    },
    {
      title: t("paidSumConvertation"),
      dataIndex: "intConv",
      key: "intConv",
      render: (text, record) => {
        return (
          <span>{`${numberWithSpacesIntl(text)} ${switchCurrency("UZS")}`}</span>
        );
      },
    },
    {
      title: t("paidSumBp"),
      dataIndex: "paymentSumBp",
      key: "paymentSumBp",
      render: (text, record) => {
        return (
          <span>{`${numberWithSpacesIntl(text)} ${switchCurrency(record.docCur)}`}</span>
        );
      },
    },
    {
      title: t("converRateRUB"),
      dataIndex: "rubRate",
      key: "rubRate",
      render: (text, record) => {
        return (
          <span>
            {checkValueToNotNull(
              text,
              "",
              `${numberWithSpacesIntl(text)} ${switchCurrency("UZS")}`,
            )}
          </span>
        );
      },
    },
    {
      title: t("converCrossRate"),
      dataIndex: "crossRate",
      key: "crossRate",
      render: (text, record) => {
        return (
          <span>
            {checkValueToNotNull(
              text,
              "",
              `${numberWithSpacesIntl(text)} ${switchCurrency("RUB")}`,
            )}
          </span>
        );
      },
    },
    {
      title: t("converUsdRate"),
      dataIndex: "usdRate",
      key: "usdRate",
      render: (text, record) => {
        return (
          <span>
            {checkValueToNotNull(
              text,
              "",
              `${numberWithSpacesIntl(text)} ${switchCurrency("UZS")}`,
            )}
          </span>
        );
      },
    },
    {
      title: t("paymSumCrosUsd"),
      dataIndex: "paymSumCrosUsd",
      key: "paymSumCrosUsd",
      render: (text, record) => {
        return (
          <span>
            {checkValueToNotNull(
              text,
              "",
              `${numberWithSpacesIntl(text)} ${switchCurrency("USD")}`,
            )}
          </span>
        );
      },
    },
    {
      title: t("sapUSDUZSRate"),
      dataIndex: "sapUsdToUzsRate",
      key: "sapUsdToUzsRate",
      render: (text, record) => {
        return (
          <span>
            {checkValueToNotNull(
              text,
              "",
              `${numberWithSpacesIntl(text)} ${switchCurrency("UZS")}`,
            )}
          </span>
        );
      },
    },
    {
      title: t("sapUSDRUBRate"),
      dataIndex: "sapUsdToRubRate",
      key: "sapUsdToRubRate",
      render: (text, record) => {
        return (
          <span>
            {checkValueToNotNull(
              text,
              "",
              `${numberWithSpacesIntl(text)} ${switchCurrency("RUB")}`,
            )}
          </span>
        );
      },
    },
    {
      title: t("status"),
      dataIndex: "status",
      key: "status",
      render: (value) => switchStatus(value),
    },
  ];

  const switchCurrency = (currency) => {
    switch (currency) {
      case "UZS":
        return "so'm";
      case "USD":
        return "$";
      case "EUR":
        return "€";
      case "RUB":
        return "₽";
      default:
        return "so'm";
    }
  };

  const switchDocType = (docType) => {
    switch (docType) {
      case "S":
        return t("supplier");
      case "A":
        return t("account");
      default:
        return docType;
    }
  };

  const switchStatus = (status) => {
    switch (status) {
      case "NOT_UPLOADED":
        return t("notUploaded");
      case "UPLOADED":
        return t("uploaded");
      default:
        return status;
    }
  };

  const handleSearchBpName = (value) => {
    debouncedSet(value, setBpNameSearch);
  };

  const intConvTotal = data.reduce(
    (sum, item) => sum + Number(removeSpaces(item.intConv)),
    0,
  );

  return (
    <LayoutAccountant>
      <div className="p-5 px-10">
        <div className="mt-10 rounded-lg border-t-4 border-t-basic bg-white p-5 drop-shadow-md">
          <span className="text-2xl font-bold text-basic">
            {t("uploadedBpAccPayments")}
          </span>
          <div className="mt-5 grid grid-cols-3 items-end gap-5">
            <div className="flex flex-col items-start gap-2">
              <span className="font-semibold text-basic">{t("startDate")}</span>
              <Input
                type="date"
                className="w-full"
                value={startDate}
                max={endDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                }}
              />
            </div>
            <div className="flex flex-col items-start gap-2">
              <span className="font-semibold text-basic">{t("endDate")}</span>
              <Input
                type="date"
                className="w-full"
                value={endDate}
                min={startDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                }}
              />
            </div>
            <div className="flex flex-col items-start gap-2">
              <span className="font-semibold text-basic">{t("Tasnif")}</span>
              <Select
                className="w-full"
                value={docType}
                onChange={(value) => {
                  setDocType(value);
                  setBpName("");
                }}
                options={[
                  { value: "", label: t("all") },
                  { value: "A", label: t("account") },
                  { value: "S", label: t("supplier") },
                ]}
              />
            </div>
            <div className="flex flex-col items-start gap-2">
              <span className="font-semibold text-basic">{t("bp/Acc")}</span>
              <AutoComplete
                className="w-full"
                value={bpName}
                options={bpData
                  .filter((item) =>
                    docType !== "" ? item.cardType === docType : item.cardType,
                  )
                  .map((item) => {
                    return {
                      value: item.cardName,
                      label: item.cardName,
                      cardName: item.cardName,
                    };
                  })}
                onChange={(value) => {
                  handleSearchBpName(value);
                  setBpName(value);

                  if (!value) {
                    setBpName("");
                  }
                }}
                onSelect={(value, label) => {
                  setBpName(label.cardName);
                  handleSearchBpName("");
                }}
                loading={bpIsLoading}
                showSearch
              />
            </div>
            <div className="flex flex-col items-start gap-2">
              <span className="font-semibold text-basic">{t("currency")}</span>
              <Select
                className="w-full"
                value={currency}
                onChange={(value) => {
                  setCurrency(value);
                }}
                options={[
                  { value: "", label: t("all") },
                  { value: "USD", label: "USD" },
                  { value: "RUB", label: "RUB" },
                ]}
              />
            </div>
          </div>
        </div>
        <div className="mt-10 rounded-lg border-t-4 border-t-basic bg-white drop-shadow-md">
          <div className="p-5">
            {/* Table */}
            <div className="overflow-x-auto">
              <Table
                columns={columns}
                dataSource={data}
                className="mt-10"
                pagination={false}
                scroll={{ x: "max-content" }}
                loading={isLoading}
                rowKey={"docEntry"}
              />
            </div>

            {/* Info Total  */}
            <div className="mt-9 flex items-center justify-end gap-10">
              <div className="flex items-center gap-5">
                <span className="font-semibold">{t("conversionTotal")} :</span>
                <Input
                  value={numberWithSpacesIntl(intConvTotal)}
                  className="h-9 w-auto border-[1px] border-[#D0D0D0]"
                  suffix={switchCurrency("UZS")}
                  disabled
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </LayoutAccountant>
  );
};

export default ConversionAccountantUploaded;
